import React from "react";

/**
 * Vehicle diagram for damage-marker placement (top / front / rear / side views).
 * Artwork: blueprint line-art PNGs (transparent) in /public/car-diagram/.
 * Clicks are stored as x/y PERCENTAGES of the svg box, so stored data stays
 * compatible across artwork changes.
 */
const SEV = {
  minor: { color: "#38bdf8", label: "Minor" },
  moderate: { color: "#f59e0b", label: "Moderate" },
  major: { color: "#ef4444", label: "Major" },
};
const sevColor = (s) => (SEV[s] || SEV.minor).color;

// Placement of each artwork inside the 200x150 viewBox (aspect-preserved, centered)
const ART = {
  top: { src: "/car-diagram/top.png", x: 68.9, y: 4, w: 62.2, h: 142 },
  front: { src: "/car-diagram/front.png", x: 25, y: 19, w: 150, h: 112 },
  rear: { src: "/car-diagram/rear.png", x: 25, y: 19, w: 150, h: 112 },
  side: { src: "/car-diagram/side.png", x: 5, y: 43.6, w: 190, h: 62.8 },
  passenger: { src: "/car-diagram/side-passenger.png", x: 5, y: 43.6, w: 190, h: 62.8 },
  interior: { src: "/car-diagram/interior.png", x: 52.3, y: 4, w: 95.4, h: 142 },
};
const artFor = (view) => ART[view] || ART.side;

export default function CarDiagram({ view = "top", markers = [], onAdd, onRemove, className = "", readonly = false }) {
  const svgRef = React.useRef(null);
  const [hoverPos, setHoverPos] = React.useState(null);

  const pct = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100)),
      y: Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100)),
    };
  };
  const handleClick = (e) => {
    if (readonly || !onAdd) return;
    onAdd({ view, ...pct(e) });
  };
  const handleMove = (e) => {
    if (readonly) return;
    setHoverPos(pct(e));
  };

  const shown = markers.filter((m) => m.view === view);
  const art = artFor(view);

  return (
    <div className={`relative w-full select-none ${className}`} data-testid={`car-diagram-${view}`}>
      <svg
        ref={svgRef}
        viewBox="0 0 200 150"
        preserveAspectRatio="xMidYMid meet"
        className={`w-full aspect-[4/3] rounded-2xl border border-white/10 ${readonly ? "" : "cursor-crosshair"}`}
        style={{ background: "radial-gradient(ellipse at 50% 35%, rgba(255,255,255,0.05), rgba(255,255,255,0.015) 70%)" }}
        onClick={handleClick}
        onMouseMove={handleMove}
        onMouseLeave={() => setHoverPos(null)}
      >
        <image
          href={art.src}
          x={art.x} y={art.y} width={art.w} height={art.h}
          preserveAspectRatio="xMidYMid meet"
          pointerEvents="none"
          style={{ filter: "drop-shadow(0 0 5px rgba(148,180,255,0.16))" }}
        />

        {/* Hover crosshair */}
        {hoverPos && !readonly && (
          <g pointerEvents="none">
            <line x1={hoverPos.x * 2} y1="0" x2={hoverPos.x * 2} y2="150" stroke="rgba(141,178,255,0.35)" strokeWidth="0.5" strokeDasharray="3 3" />
            <line x1="0" y1={hoverPos.y * 1.5} x2="200" y2={hoverPos.y * 1.5} stroke="rgba(141,178,255,0.35)" strokeWidth="0.5" strokeDasharray="3 3" />
            <circle cx={hoverPos.x * 2} cy={hoverPos.y * 1.5} r="4" fill="none" stroke="rgba(141,178,255,0.8)" strokeWidth="0.8" />
          </g>
        )}

        {/* Markers — numbered, severity-colored */}
        {shown.map((m, i) => {
          const c = sevColor(m.severity);
          const cx = (m.x / 100) * 200;
          const cy = (m.y / 100) * 150;
          return (
            <g key={m.id} transform={`translate(${cx}, ${cy})`}>
              <circle r="9" fill={c} opacity="0.14" />
              <circle r="6.4" fill="none" stroke={c} strokeWidth="0.9" opacity="0.8" />
              <circle r="4.3" fill={c} stroke="#fff" strokeWidth="0.9" />
              <text y="0.2" textAnchor="middle" dominantBaseline="central" fontSize="4.8" fontWeight="700" fill="#fff" pointerEvents="none">
                {i + 1}
              </text>
              {m.photo_ids?.length > 0 && (
                <g transform="translate(8,-8)" pointerEvents="none" data-testid={`marker-photo-badge-${m.id}`}>
                  <circle r="3.2" fill="#0a0c10" stroke={c} strokeWidth="0.6" />
                  <rect x="-1.6" y="-1.1" width="3.2" height="2.4" rx="0.6" fill="none" stroke="#fff" strokeWidth="0.5" />
                  <circle cx="0" cy="0.1" r="0.65" fill="none" stroke="#fff" strokeWidth="0.45" />
                </g>
              )}
              {!readonly && onRemove && (
                <circle r="11" fill="transparent" style={{ cursor: "pointer" }}
                  onClick={(e) => { e.stopPropagation(); onRemove(m); }} />
              )}
            </g>
          );
        })}
      </svg>
      <div className="absolute top-3 left-4 font-mono-alt text-[10px] text-white/60 uppercase tracking-widest pointer-events-none bg-black/30 backdrop-blur-sm px-2 py-1 rounded-md">
        {view} view
      </div>
      {!readonly && (
        <div className="absolute bottom-3 right-4 flex items-center gap-3 pointer-events-none bg-black/30 backdrop-blur-sm px-2.5 py-1.5 rounded-md">
          {Object.values(SEV).map((s) => (
            <span key={s.label} className="flex items-center gap-1.5 font-mono-alt text-[9px] text-white/55 uppercase tracking-wider">
              <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
              {s.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
